/** @typedef {import ('preprocessor').SquareMatrix} SquareMatrix */ import {
  QRVersion,
  QRCodeOptions,
} from 'qr_utils';

// actually I am not sure whether this should be included in the encoder or not, currently encoder will still do calculation for this lookup table
const FORMAT_INFO_TABLE = [
  { bits: 0x5412, formatInfo: { level: 'M', dataMask: 0 } },
  { bits: 0x5125, formatInfo: { level: 'M', dataMask: 1 } },
  { bits: 0x5e7c, formatInfo: { level: 'M', dataMask: 2 } },
  { bits: 0x5b4b, formatInfo: { level: 'M', dataMask: 3 } },
  { bits: 0x45f9, formatInfo: { level: 'M', dataMask: 4 } },
  { bits: 0x40ce, formatInfo: { level: 'M', dataMask: 5 } },
  { bits: 0x4f97, formatInfo: { level: 'M', dataMask: 6 } },
  { bits: 0x4aa0, formatInfo: { level: 'M', dataMask: 7 } },
  { bits: 0x77c4, formatInfo: { level: 'L', dataMask: 0 } },
  { bits: 0x72f3, formatInfo: { level: 'L', dataMask: 1 } },
  { bits: 0x7daa, formatInfo: { level: 'L', dataMask: 2 } },
  { bits: 0x789d, formatInfo: { level: 'L', dataMask: 3 } },
  { bits: 0x662f, formatInfo: { level: 'L', dataMask: 4 } },
  { bits: 0x6318, formatInfo: { level: 'L', dataMask: 5 } },
  { bits: 0x6c41, formatInfo: { level: 'L', dataMask: 6 } },
  { bits: 0x6976, formatInfo: { level: 'L', dataMask: 7 } },
  { bits: 0x1689, formatInfo: { level: 'H', dataMask: 0 } },
  { bits: 0x13be, formatInfo: { level: 'H', dataMask: 1 } },
  { bits: 0x1ce7, formatInfo: { level: 'H', dataMask: 2 } },
  { bits: 0x19d0, formatInfo: { level: 'H', dataMask: 3 } },
  { bits: 0x0762, formatInfo: { level: 'H', dataMask: 4 } },
  { bits: 0x0255, formatInfo: { level: 'H', dataMask: 5 } },
  { bits: 0x0d0c, formatInfo: { level: 'H', dataMask: 6 } },
  { bits: 0x083b, formatInfo: { level: 'H', dataMask: 7 } },
  { bits: 0x355f, formatInfo: { level: 'Q', dataMask: 0 } },
  { bits: 0x3068, formatInfo: { level: 'Q', dataMask: 1 } },
  { bits: 0x3f31, formatInfo: { level: 'Q', dataMask: 2 } },
  { bits: 0x3a06, formatInfo: { level: 'Q', dataMask: 3 } },
  { bits: 0x24b4, formatInfo: { level: 'Q', dataMask: 4 } },
  { bits: 0x2183, formatInfo: { level: 'Q', dataMask: 5 } },
  { bits: 0x2eda, formatInfo: { level: 'Q', dataMask: 6 } },
  { bits: 0x2bed, formatInfo: { level: 'Q', dataMask: 7 } },
];

/**
 * @param {number} x - The first number
 * @param {number} y - The second number
 * @returns {number} The number of bits differing between x and y
 */
function numBitsDiffering(x, y) {
  let z = x ^ y;
  let bitCount = 0;
  while (z) {
    bitCount++;
    z &= z - 1;
  }
  return bitCount;
}

/**
 * @param {SquareMatrix} matrix - The extracted image (maybe should not use this name, but it has the same underlying functionality)
 * @returns {QRVersion | null} The version of the QR code, return null if cannot retrieve the version
 */
export function readVersion(matrix) {
  const dimension = matrix.width;
  const provisionalVersion = Math.floor((dimension - 17) / 4);
  if (provisionalVersion < 1 || provisionalVersion > 40) {
    return null;
  }
  const qrVersion = new QRVersion(provisionalVersion); // we will compare this with the value we get from the QR code
  const expectedVersionBits = qrVersion.getVersionBits();

  // if the version is less than 7, we cannot retrieve the version, the check is inside QRVersion
  if (expectedVersionBits === null) {
    return qrVersion;
  }

  let topRightVersionBits = 0;
  for (let y = 5; y >= 0; y--) {
    for (let x = dimension - 9; x >= dimension - 11; x--) {
      topRightVersionBits = (topRightVersionBits << 1) | matrix.get(x, y);
    }
  }

  let bottomLeftVersionBits = 0;
  for (let x = 5; x >= 0; x--) {
    for (let y = dimension - 9; y >= dimension - 11; y--) {
      bottomLeftVersionBits = (bottomLeftVersionBits << 1) | matrix.get(x, y);
    }
  }

  // either top right or bottom left should within 3 bits off from expected version bits, since that is the most error correction we can handle
  if (
    expectedVersionBits === topRightVersionBits ||
    expectedVersionBits === bottomLeftVersionBits
  ) {
    return qrVersion;
  }

  const bestDiff = [topRightVersionBits, bottomLeftVersionBits]
    .map((versionBits) => {
      return numBitsDiffering(expectedVersionBits, versionBits);
    })
    .reduce((acc, val) => Math.min(acc, val), 18);

  if (bestDiff <= 3) {
    return qrVersion;
  }

  return null;
}

/**
 * @typedef {Object} FormatInfo
 * @property {number} level - The error correction level
 * @property {number} dataMask - The data mask
 */

/**
 * @param {SquareMatrix} matrix - The extracted matrix
 * @returns {QRCodeOptions | null} The format information of the QR code, return null if cannot retrieve the format information
 */
export function readFormatInformation(matrix) {
  let topLeftFormatInfoBits = 0;
  for (let x = 0; x <= 8; x++) {
    if (x !== 6) {
      // Skip timing pattern bit
      topLeftFormatInfoBits = (topLeftFormatInfoBits << 1) | matrix.get(x, 8);
    }
  }
  for (let y = 7; y >= 0; y--) {
    if (y !== 6) {
      // Skip timing pattern bit
      topLeftFormatInfoBits = (topLeftFormatInfoBits << 1) | matrix.get(8, y);
    }
  }

  const dimension = matrix.width;
  let topRightBottomLeftFormatInfoBits = 0;
  for (let y = dimension - 1; y >= dimension - 7; y--) {
    // bottom left
    topRightBottomLeftFormatInfoBits =
      (topRightBottomLeftFormatInfoBits << 1) | matrix.get(8, y);
  }
  for (let x = dimension - 8; x < dimension; x++) {
    // top right
    topRightBottomLeftFormatInfoBits =
      (topRightBottomLeftFormatInfoBits << 1) | matrix.get(x, 8);
  }

  let bestDiff = 15;

  /** @type {FormatInfo | null} */
  let bestFormatInfo = null;
  for (const { bits, formatInfo } of FORMAT_INFO_TABLE) {
    if (
      bits === topLeftFormatInfoBits ||
      bits === topRightBottomLeftFormatInfoBits
    ) {
      return QRCodeOptions.from(formatInfo.level, formatInfo.dataMask);
    }

    // calculate the difference between the bits
    const diff = numBitsDiffering(bits, topLeftFormatInfoBits);

    if (diff <= 3 && diff < bestDiff) {
      bestDiff = diff;
      bestFormatInfo = formatInfo;
    }
    const diff2 = numBitsDiffering(bits, topRightBottomLeftFormatInfoBits);

    if (diff2 <= 3 && diff2 < bestDiff) {
      bestDiff = diff2;
      bestFormatInfo = formatInfo;
    }
  }

  return bestFormatInfo === null
    ? null
    : QRCodeOptions.from(bestFormatInfo.level, bestFormatInfo.dataMask);
}
/**
 * @typedef {Object} RsBlockTable
 * @property {number} num_blocks - The number of blocks
 * @property {number} data_codewords - The number of data codewords
 * @property {number} ecc_codewords - The number of ecc codewords
 */

/**
 * @param {RsBlockTable[]} rsBlockTables - The RS block tables
 * @returns {function(Uint8Array):number[]} the dataBlocks
 */
export const getDataBlocks = (rsBlockTables) => (codewords) => {
  const ecInfo = {
    ecCodewordsPerBlock: rsBlockTables[0].ecc_codewords,
    ecBlocks: rsBlockTables.map((block) => {
      return {
        numBlocks: block.num_blocks,
        dataCodewordsPerBlock: block.data_codewords,
      };
    }),
  };

  const dataBlocks = [];
  ecInfo.ecBlocks.forEach((block) => {
    for (let i = 0; i < block.numBlocks; i++) {
      dataBlocks.push({
        numDataCodewords: block.dataCodewordsPerBlock,
        codewords: [],
      });
    }
  });
  // need to ensure this is an array, but previously I passed in Uint8Array
  const l_codewords = [...codewords];
  const shortBlockSize = ecInfo.ecBlocks[0].dataCodewordsPerBlock;
  // Pull codewords to fill the blocks up to the minimum size
  for (let i = 0; i < shortBlockSize; i++) {
    for (const dataBlock of dataBlocks) {
      dataBlock.codewords.push(l_codewords.shift());
    }
  }

  // If there are any large blocks, pull codewords to fill the last element of those
  if (ecInfo.ecBlocks.length > 1) {
    const smallBlockCount = ecInfo.ecBlocks[0].numBlocks;
    const largeBlockCount = ecInfo.ecBlocks[1].numBlocks;
    for (let i = 0; i < largeBlockCount; i++) {
      dataBlocks[smallBlockCount + i].codewords.push(l_codewords.shift());
    }
  }

  // Add the rest of the codewords to the blocks. These are the error correction codewords.
  while (l_codewords.length > 0) {
    for (const dataBlock of dataBlocks) {
      dataBlock.codewords.push(l_codewords.shift());
    }
  }

  return dataBlocks;
};
